import { useState, useCallback } from "react";
import type { GameMode } from "../types/galaxy";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

interface WarpTarget {
  systemId: string;
  systemName: string;
  missionId?: string;
}

export function useGameMode() {
  const [gameMode, setGameMode] = useState<GameMode>("galaxy_map");
  const [warpTarget, setWarpTarget] = useState<WarpTarget | null>(null);
  const [isWarping, setIsWarping] = useState(false);

  // Track the encounter session created by warping
  const [encounterSession, setEncounterSession] = useState<{
    sessionId: string;
    encounterState: any;
    opening: any;
  } | null>(null);

  // Track replay session ID
  const [replaySessionId, setReplaySessionId] = useState<string | null>(null);

  const initiateWarp = useCallback(async (target: WarpTarget) => {
    setWarpTarget(target);
    setGameMode("warp_transition");
    setIsWarping(true);

    try {
      // Start the encounter while the transition plays
      const res = await fetch(`${API_BASE}/api/start-encounter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          systemId: target.systemId,
          missionId: target.missionId,
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to start encounter: ${res.status}`);
      }

      const data = await res.json();
      setEncounterSession(data);

      // Let the warp transition animation play for at least 2 seconds
      await new Promise(r => setTimeout(r, 2000));

      setGameMode("bridge");
      setIsWarping(false);
    } catch (err) {
      console.error("Warp failed:", err);
      setGameMode("galaxy_map");
      setIsWarping(false);
      setWarpTarget(null);
    }
  }, []);

  const returnToMap = useCallback(() => {
    setGameMode("galaxy_map");
    setWarpTarget(null);
    setEncounterSession(null);
    setReplaySessionId(null);
  }, []);

  const startReplay = useCallback((sessionId: string) => {
    setReplaySessionId(sessionId);
    setGameMode("replay");
  }, []);

  return {
    gameMode,
    warpTarget,
    isWarping,
    encounterSession,
    replaySessionId,
    initiateWarp,
    returnToMap,
    startReplay,
    setGameMode,
  };
}
